<template>
  <div class="flex h-screen bg-gray-50 overflow-hidden">
    <!-- Sidebar -->
    <aside class="bg-gradient-to-b from-gray-900 to-gray-800 relative text-white w-64 flex flex-col shadow-2xl flex-shrink-0">
      <!-- Logo -->
      <div class="p-6 border-b border-gray-700">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-[#8C0004] rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon icon="solar:code-square-bold" class="w-6 h-6" />
          </div>
          <div>
            <h1 class="font-bold text-lg">Youdégo</h1>
            <p class="text-xs text-gray-400">Portail Partenaire</p>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 py-6 overflow-y-auto">
        <ul class="space-y-1 px-3">
          <li v-for="item in menuItems" :key="item.name">
            <router-link
              :to="item.path"
              :class="[
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                isActive(item.path)
                  ? 'bg-[#8C0004] text-white shadow-lg'
                  : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
              ]"
            >
              <Icon :icon="item.icon" class="w-6 h-6 flex-shrink-0" />
              <span class="font-medium">{{ item.name }}</span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- Profil -->
      <div class="p-4 border-t border-gray-700">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-gradient-to-br from-[#8C0004] to-red-800 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon icon="solar:user-bold" class="w-5 h-5" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm truncate">{{ authStore.fullName }}</p>
            <p class="text-xs text-gray-400 truncate">
              {{ authStore.isOwner ? 'Propriétaire' : 'Développeur' }}
            </p>
          </div>
        </div>
        <button
          @click="logout"
          class="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors text-sm"
        >
          <Icon icon="solar:logout-2-bold" class="w-5 h-5" />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>

    <!-- Contenu principal -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Bandeau mode test -->
      <div
        v-if="modeStore.isTest"
        class="bg-amber-100 border-b border-amber-300 px-8 py-2 flex items-center gap-2 text-amber-900 text-sm"
      >
        <Icon icon="solar:test-tube-bold" class="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Mode test</strong> — vous travaillez dans le bac à sable :
          les livraisons sont simulées, aucun livreur réel n'est sollicité et aucun paiement n'est encaissé.
        </span>
      </div>

      <!-- Bandeau compte suspendu -->
      <div
        v-if="authStore.isSuspended"
        class="bg-red-100 border-b border-red-300 px-8 py-2 flex items-center gap-2 text-red-900 text-sm"
      >
        <Icon icon="solar:danger-triangle-bold" class="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Compte suspendu</strong> — vos clés API sont refusées et le portail est en lecture seule.
          Contactez le support Youdégo.
        </span>
      </div>

      <!-- Header -->
      <header class="bg-white shadow-sm border-b border-gray-200 px-8 py-4">
        <div class="flex items-center justify-between gap-4">
          <div class="min-w-0">
            <h2 class="text-2xl font-bold text-gray-800">{{ pageTitle }}</h2>
            <p class="text-sm text-gray-500 mt-1">{{ pageSubtitle }}</p>
          </div>

          <!-- Sélecteur de mode -->
          <div class="flex items-center gap-3 flex-shrink-0">
            <div class="text-right hidden md:block">
              <p class="text-sm font-semibold text-gray-800 truncate max-w-[200px]">
                {{ authStore.partner?.name }}
              </p>
              <p class="text-xs text-gray-500">Environnement</p>
            </div>
            <div class="flex bg-gray-100 rounded-lg p-1">
              <button
                @click="switchMode('TEST')"
                :class="[
                  'px-3 py-1.5 rounded-md text-sm font-semibold transition-colors',
                  modeStore.isTest
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                Test
              </button>
              <button
                @click="switchMode('LIVE')"
                :class="[
                  'px-3 py-1.5 rounded-md text-sm font-semibold transition-colors',
                  modeStore.isLive
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                Live
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Page -->
      <main class="flex-1 overflow-y-auto p-8">
        <router-view :key="modeStore.mode" />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Icon } from '@iconify/vue'
import { useAuthStore } from '../store/auth'
import { useModeStore } from '../store/mode'
import type { ApiMode } from '../services/api'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const modeStore = useModeStore()

const menuItems = [
  { name: 'Tableau de bord', path: '/dashboard', icon: 'solar:chart-2-bold' },
  { name: 'Livraisons', path: '/orders', icon: 'solar:box-bold' },
  { name: 'Clés API', path: '/api-keys', icon: 'solar:key-bold' },
  { name: 'Webhooks', path: '/webhooks', icon: 'solar:bell-bold' },
  { name: 'Documentation', path: '/docs', icon: 'solar:document-text-bold' },
  { name: 'Paramètres', path: '/settings', icon: 'solar:settings-bold' },
]

const pageSubtitles: Record<string, string> = {
  '/dashboard': 'Vue d\'ensemble de votre activité',
  '/orders': 'Les livraisons créées via votre intégration',
  '/api-keys': 'Créez et révoquez les clés qui authentifient vos requêtes',
  '/webhooks': 'Recevez les événements de livraison et de paiement en temps réel',
  '/docs': 'Référence de l\'API et console de test',
  '/settings': 'Votre compte, votre équipe et votre facturation',
}

const pageTitle = computed(() => {
  const item = menuItems.find((i) => i.path === route.path)
  return item?.name || 'Portail Partenaire'
})

const pageSubtitle = computed(() => pageSubtitles[route.path] || '')

function isActive(path: string): boolean {
  return route.path === path
}

function switchMode(mode: ApiMode) {
  if (modeStore.mode === mode) return
  modeStore.setMode(mode)
}

async function logout() {
  await authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
